import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronDown, ChevronUp, AlertCircle, AlertTriangle, Info, RefreshCw } from "lucide-react";
import type { ServerError } from "@shared/schema";

function formatDate(date: string | Date | null | undefined) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit",
  });
}

const LEVEL_VARIANTS: Record<string, "destructive" | "outline" | "secondary"> = {
  error: "destructive",
  warn: "outline",
  info: "secondary",
};

const LEVEL_ICONS: Record<string, typeof AlertCircle> = {
  error: AlertCircle,
  warn: AlertTriangle,
  info: Info,
};

function ErrorRow({ item }: { item: ServerError }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = LEVEL_ICONS[item.level] ?? AlertCircle;

  return (
    <div className="border-b last:border-b-0 py-3 px-4">
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge variant={LEVEL_VARIANTS[item.level] ?? "secondary"} className="shrink-0">
              {item.level}
            </Badge>
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground shrink-0">
              {item.context}
            </span>
            <span className="text-xs text-muted-foreground ml-auto shrink-0">{formatDate(item.createdAt)}</span>
          </div>
          <p className="text-sm break-words">{item.message}</p>
          {item.userId && (
            <p className="text-xs text-muted-foreground mt-0.5">User: {item.userId}</p>
          )}
          {item.details != null && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-7 px-2 text-xs"
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              {expanded ? "Hide details" : "Show details"}
            </Button>
          )}
          {expanded && item.details != null && (
            <pre className="mt-2 p-3 rounded-md bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(item.details, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminErrorLog() {
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [contextFilter, setContextFilter] = useState<string>("all");

  const { data, isLoading, refetch, isRefetching } = useQuery<{ errors: ServerError[]; total: number }>({
    queryKey: ["/api/admin/errors", levelFilter, contextFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "200" });
      if (levelFilter !== "all") params.set("level", levelFilter);
      if (contextFilter !== "all") params.set("context", contextFilter);
      const _tok = localStorage.getItem("auth_token");
      const res = await fetch(`/api/admin/errors?${params}`, {
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { Authorization: `Bearer ${_tok}` } : {},
      });
      if (!res.ok) throw new Error("Failed to fetch error log");
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const errors = data?.errors ?? [];

  const allContexts = Array.from(new Set(errors.map(e => e.context))).sort();

  const filtered = errors.filter(e =>
    (levelFilter === "all" || e.level === levelFilter) &&
    (contextFilter === "all" || e.context === contextFilter)
  );

  const errorCount = errors.filter(e => e.level === "error").length;
  const warnCount = errors.filter(e => e.level === "warn").length;

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Server Error Log</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Last 200 entries — auto-refreshes every 30 seconds
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            {isRefetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Card className="flex-1 min-w-[120px]">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-muted-foreground">Errors</p>
              <p className="text-2xl font-bold text-destructive">{errorCount}</p>
            </CardContent>
          </Card>
          <Card className="flex-1 min-w-[120px]">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-muted-foreground">Warnings</p>
              <p className="text-2xl font-bold">{warnCount}</p>
            </CardContent>
          </Card>
          <Card className="flex-1 min-w-[120px]">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{errors.length}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap gap-2">
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="warn">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
          <Select value={contextFilter} onValueChange={setContextFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All contexts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All contexts</SelectItem>
              {allContexts.map(ctx => (
                <SelectItem key={ctx} value={ctx}>{ctx}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No errors logged yet.
              </div>
            ) : (
              <div>
                {filtered.map(item => (
                  <ErrorRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
